import React from "react"
import { useTranslation } from "react-i18next"

import CheckRoundedIcon from "@mui/icons-material/CheckRounded"
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"

import type { CopyIconButtonProps } from "./copy-icon-button.types"

const COPY_FEEDBACK_DURATION_MS = 2000

const copyText = async ({
  onCopy,
  resolveValue,
  setHasCopied,
  value,
}: {
  onCopy?: (copiedValue: string) => void
  resolveValue?: () => Promise<string>
  setHasCopied: (hasCopied: boolean) => void
  value: string
}): Promise<void> => {
  try {
    const text = (await resolveValue?.()) ?? value
    if (text.trim() === "") {
      return
    }

    await navigator.clipboard.writeText(text)
    setHasCopied(true)
    await Promise.resolve(onCopy?.(text))
  } catch {
    // ignore clipboard / resolve errors
  }
}

export const CopyIconButton: React.FC<CopyIconButtonProps> = ({
  ariaLabel,
  color,
  disabled = false,
  edge,
  onCopy,
  resolveValue,
  successMessage,
  value = "",
}) => {
  const { t } = useTranslation("common")
  const [hasCopied, setHasCopied] = React.useState(false)

  React.useEffect(() => {
    if (!hasCopied) {
      return
    }

    const timeoutId = globalThis.setTimeout(() => {
      setHasCopied(false)
    }, COPY_FEEDBACK_DURATION_MS)

    return () => {
      globalThis.clearTimeout(timeoutId)
    }
  }, [hasCopied])

  const handleClick = () => {
    void copyText({
      onCopy,
      resolveValue,
      setHasCopied,
      value,
    })
  }

  return (
    <Tooltip
      disableInteractive
      open={hasCopied}
      placement="top"
      slotProps={{
        popper: {
          disablePortal: true,
        },
      }}
      title={successMessage ?? t("clipboard.copySuccess")}
    >
      <span>
        <IconButton
          aria-label={ariaLabel}
          color={color}
          disabled={disabled || (resolveValue == null && value.trim() === "")}
          edge={edge}
          onClick={handleClick}
        >
          {hasCopied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
        </IconButton>
      </span>
    </Tooltip>
  )
}
